import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Flag, Info, Pause, Play, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  ESWT_PROTOCOL_METADATA,
  ESWT_SPEED_PERCENT_OPTIONS,
  ISWT_PROTOCOL_METADATA,
  buildDefaultESWTProtocol,
  buildESWTPacingEvents,
  buildESWTPayload,
  buildISWTPacingEvents,
  buildISWTPayload,
  calculateISWTCompletedShuttles,
  calculateISWTDistanceMeters,
  formatClock,
  formatISWTLevelLabel,
  getISWTLevelDefinition,
  normalizeESWTPayload,
  normalizeISWTPayload,
  resolveESWTInterpretation,
  resolveISWTInterpretation,
  summarizeESWTResult,
  summarizeISWTResult,
  type ESWTProtocol,
  type ESWTResultPayload,
  type ISWTAttemptPayload,
  type ISWTResultPayload,
} from '../shuttle-walk/shuttleWalkDomain';
import { formatBorgValue, type StepTestStopReason, type StepTestTimerBlock, type StepTestVitalsBlock } from '../step-tests/stepTestsDomain';
import { useScheduledBeeps } from './useScheduledBeeps';

type PatientContextInput = {
  birthDate?: string | null;
  ageYears?: number | null;
  sex?: string | null;
  weightKg?: number | null;
  heightCm?: number | null;
  identifier?: string | null;
};

type VitalsFormState = {
  hr: string;
  spo2: string;
  bpSys: string;
  bpDia: string;
  borgDyspnea: number | null;
  borgLegFatigue: number | null;
};

type ISWTAttemptFormState = {
  attemptNumber: number;
  baseline: VitalsFormState;
  final: VitalsFormState;
  completedLevel: string;
  completedShuttlesOnLevel: string;
  observedPeakHr: string;
  observedMinSpo2: string;
  stopReason: StepTestStopReason;
  notes: string;
  elapsedSeconds: number;
  startedAt: string | null;
  endedAt: string | null;
};

type ESWTFormState = {
  baseline: VitalsFormState;
  final: VitalsFormState;
  sourceIswtSpeedKmh: string;
  sourceIswtLevel: string;
  speedPercentOfIswt: number;
  observedPeakHr: string;
  observedMinSpo2: string;
  stopReason: StepTestStopReason;
  notes: string;
  elapsedSeconds: number;
  startedAt: string | null;
  endedAt: string | null;
};

interface ISWTTestProps {
  initial?: ISWTResultPayload | null;
  patientContext?: PatientContextInput;
  onCancel?: () => void;
  onNext: (payload: ISWTResultPayload) => void;
}

interface ESWTReferenceInput {
  peakLevelReached?: number | null;
  peakSpeedKmh?: number | null;
}

interface ESWTTestProps {
  initial?: ESWTResultPayload | null;
  patientContext?: PatientContextInput;
  iswtReference?: ESWTReferenceInput | null;
  onCancel?: () => void;
  onNext: (payload: ESWTResultPayload) => void;
}

export type { ESWTResultPayload, ISWTResultPayload };

const BORG_VALUES: number[] = [0, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const STOP_REASON_OPTIONS: Array<{ value: StepTestStopReason; label: string }> = [
  { value: 'pace_loss', label: 'Perda do pacing' },
  { value: 'symptom_limitation', label: 'Limitação por sintomas' },
  { value: 'patient_request', label: 'Pedido do paciente' },
  { value: 'safety_stop', label: 'Critério de segurança' },
  { value: 'supervisor_interruption', label: 'Interrupção do avaliador' },
  { value: 'completed', label: 'Completou a proposta' },
];

const ISWT_MAX_DURATION_SECONDS = 12 * 60;

const parseNullableNumber = (value: string): number | null => {
  const trimmed = value.trim().replace(',', '.');
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseInteger = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildVitalsState = (value?: StepTestVitalsBlock | null): VitalsFormState => ({
  hr: value?.hr ?? '',
  spo2: value?.spo2 ?? '',
  bpSys: value?.bp.sys ?? '',
  bpDia: value?.bp.dia ?? '',
  borgDyspnea: value?.borg.dyspnea ?? null,
  borgLegFatigue: value?.borg.legFatigue ?? null,
});

const buildBlankVitalsState = (): VitalsFormState => buildVitalsState();

const toVitalsPayload = (state: VitalsFormState): StepTestVitalsBlock => ({
  hr: state.hr.trim() || null,
  spo2: state.spo2.trim() || null,
  bp: {
    sys: state.bpSys.trim() || null,
    dia: state.bpDia.trim() || null,
  },
  borg: {
    dyspnea: state.borgDyspnea,
    legFatigue: state.borgLegFatigue,
  },
});

const MetricCard = ({ label, value, note }: { label: string; value: string; note?: string | null }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-2xl font-black text-slate-900">{value}</div>
    {note ? <div className="mt-2 text-sm text-slate-500">{note}</div> : null}
  </div>
);

function BorgSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  return (
    <label className="space-y-1">
      <div className="text-xs font-medium text-slate-600">{label}</div>
      <select
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
        value={value == null ? '' : String(value)}
        onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
      >
        <option value="">Não informado</option>
        {BORG_VALUES.map((item) => (
          <option key={`${label}-${item}`} value={item}>
            {String(item).replace('.', ',')}
          </option>
        ))}
      </select>
    </label>
  );
}

function VitalsEditor({
  title,
  state,
  onChange,
}: {
  title: string;
  state: VitalsFormState;
  onChange: (patch: Partial<VitalsFormState>) => void;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 text-xs font-black uppercase tracking-[0.24em] text-slate-400">{title}</div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <label className="space-y-1">
          <div className="text-xs font-medium text-slate-600">FC (bpm)</div>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={state.hr} onChange={(event) => onChange({ hr: event.target.value })} />
        </label>
        <label className="space-y-1">
          <div className="text-xs font-medium text-slate-600">SpO2 (%)</div>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={state.spo2} onChange={(event) => onChange({ spo2: event.target.value })} />
        </label>
        <label className="space-y-1">
          <div className="text-xs font-medium text-slate-600">PAS (mmHg)</div>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={state.bpSys} onChange={(event) => onChange({ bpSys: event.target.value })} />
        </label>
        <label className="space-y-1">
          <div className="text-xs font-medium text-slate-600">PAD (mmHg)</div>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={state.bpDia} onChange={(event) => onChange({ bpDia: event.target.value })} />
        </label>
        <BorgSelect label="Borg dispneia" value={state.borgDyspnea} onChange={(value) => onChange({ borgDyspnea: value })} />
        <BorgSelect label="Borg fadiga de MMII" value={state.borgLegFatigue} onChange={(value) => onChange({ borgLegFatigue: value })} />
      </div>
    </div>
  );
}

function PacerControls({
  running,
  soundEnabled,
  elapsedSeconds,
  maxDurationSeconds,
  onStart,
  onPause,
  onFinish,
  onReset,
  onToggleSound,
}: {
  running: boolean;
  soundEnabled: boolean;
  elapsedSeconds: number;
  maxDurationSeconds: number;
  onStart: () => void;
  onPause: () => void;
  onFinish: () => void;
  onReset: () => void;
  onToggleSound: () => void;
}) {
  const isStarted = elapsedSeconds > 0;

  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">Pacer sintético</div>
          <div className="mt-2 text-3xl font-black text-slate-900">{formatClock(elapsedSeconds)}</div>
          <div className="mt-1 text-sm text-slate-500">Teto operacional: {formatClock(maxDurationSeconds)}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
            onClick={onStart}
          >
            <Play className="h-4 w-4" />
            {running ? 'Rodando' : isStarted ? 'Retomar' : 'Iniciar'}
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
            onClick={onPause}
            disabled={!running}
          >
            <Pause className="h-4 w-4" />
            Pausar
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
            onClick={onFinish}
            disabled={!isStarted}
          >
            <Flag className="h-4 w-4" />
            Encerrar
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
            onClick={onReset}
          >
            <RotateCcw className="h-4 w-4" />
            Resetar
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
            onClick={onToggleSound}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            {soundEnabled ? 'Áudio ligado' : 'Áudio desligado'}
          </button>
        </div>
      </div>
    </div>
  );
}

const buildISWTAttemptState = (attempt?: ISWTAttemptPayload | null, attemptNumber = 1): ISWTAttemptFormState => ({
  attemptNumber: attempt?.attemptNumber ?? attemptNumber,
  baseline: buildVitalsState(attempt?.baseline),
  final: buildVitalsState(attempt?.final),
  completedLevel: attempt?.completedLevel ? String(attempt.completedLevel) : '',
  completedShuttlesOnLevel:
    typeof attempt?.completedShuttlesOnLevel === 'number' ? String(attempt.completedShuttlesOnLevel) : '',
  observedPeakHr: attempt?.observedPeakHr != null ? String(attempt.observedPeakHr) : '',
  observedMinSpo2: attempt?.observedMinSpo2 != null ? String(attempt.observedMinSpo2) : '',
  stopReason: attempt?.stopReason ?? 'pace_loss',
  notes: attempt?.notes ?? '',
  elapsedSeconds: attempt?.timer.elapsed_s ?? 0,
  startedAt: attempt?.timer.startedAt ?? null,
  endedAt: attempt?.timer.endedAt ?? null,
});

const buildESWTState = (payload?: ESWTResultPayload | null, reference?: ESWTReferenceInput | null): ESWTFormState => ({
  baseline: buildVitalsState(payload?.baseline),
  final: buildVitalsState(payload?.final),
  sourceIswtSpeedKmh:
    payload?.protocol.sourceIswtSpeedKmh != null
      ? String(payload.protocol.sourceIswtSpeedKmh)
      : reference?.peakSpeedKmh != null
        ? String(reference.peakSpeedKmh)
        : '',
  sourceIswtLevel:
    payload?.protocol.sourceIswtLevel != null
      ? String(payload.protocol.sourceIswtLevel)
      : reference?.peakLevelReached != null
        ? String(reference.peakLevelReached)
        : '',
  speedPercentOfIswt: payload?.protocol.speedPercentOfIswt ?? 85,
  observedPeakHr: payload?.observedPeakHr != null ? String(payload.observedPeakHr) : '',
  observedMinSpo2: payload?.observedMinSpo2 != null ? String(payload.observedMinSpo2) : '',
  stopReason: payload?.stopReason ?? 'patient_request',
  notes: payload?.notes ?? '',
  elapsedSeconds: payload?.timer.elapsed_s ?? 0,
  startedAt: payload?.timer.startedAt ?? null,
  endedAt: payload?.timer.endedAt ?? null,
});

const toTimerBlock = (elapsedSeconds: number, startedAt: string | null, endedAt: string | null, limitSeconds: number): StepTestTimerBlock => ({
  elapsed_s: Math.max(0, Math.round(elapsedSeconds)),
  completed: elapsedSeconds >= limitSeconds,
  startedAt,
  endedAt,
});

const getISWTCurrentLevel = (elapsedSeconds: number) => {
  const safe = Math.max(0, Math.min(ISWT_MAX_DURATION_SECONDS, elapsedSeconds));
  const level = Math.min(12, Math.floor(safe / 60) + 1);
  return getISWTLevelDefinition(level);
};

const getISWTAttemptDraft = (form: ISWTAttemptFormState): Partial<ISWTAttemptPayload> => ({
  attemptNumber: form.attemptNumber,
  baseline: toVitalsPayload(form.baseline),
  final: toVitalsPayload(form.final),
  completedLevel: parseInteger(form.completedLevel) ?? 0,
  completedShuttlesOnLevel: parseInteger(form.completedShuttlesOnLevel) ?? 0,
  observedPeakHr: parseNullableNumber(form.observedPeakHr),
  observedMinSpo2: parseNullableNumber(form.observedMinSpo2),
  stopReason: form.stopReason,
  timer: toTimerBlock(form.elapsedSeconds, form.startedAt, form.endedAt, ISWT_MAX_DURATION_SECONDS),
  notes: form.notes,
});

const hasISWTAttemptData = (form: ISWTAttemptFormState): boolean =>
  (parseInteger(form.completedLevel) ?? 0) > 0 ||
  (parseInteger(form.completedShuttlesOnLevel) ?? 0) > 0 ||
  form.elapsedSeconds > 0;

export function ISWTTest({ initial = null, patientContext, onCancel, onNext }: ISWTTestProps) {
  const normalizedInitial = normalizeISWTPayload(initial);
  const [attempts, setAttempts] = useState<ISWTAttemptFormState[]>(() => {
    const existingAttempts = normalizedInitial?.attempts ?? [];
    const count = Math.max(2, existingAttempts.length || 2);
    return Array.from({ length: count }, (_, index) => buildISWTAttemptState(existingAttempts[index], index + 1));
  });
  const [activeAttemptIndex, setActiveAttemptIndex] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const activeAttempt = attempts[activeAttemptIndex] ?? attempts[0];
  const currentLevel = getISWTCurrentLevel(activeAttempt?.elapsedSeconds ?? 0);
  const pacingEvents = useMemo(() => buildISWTPacingEvents(), []);
  const { clearScheduledTimeouts, startFrom } = useScheduledBeeps({
    events: pacingEvents,
    muted: !soundEnabled,
  });

  const timerStartedAtRef = useRef<number | null>(null);
  const accumulatedSecondsRef = useRef<number>(activeAttempt?.elapsedSeconds ?? 0);
  const timerIntervalRef = useRef<number | null>(null);

  const patchActiveAttempt = (patch: Partial<ISWTAttemptFormState>) => {
    setAttempts((current) =>
      current.map((attempt, index) => (index === activeAttemptIndex ? { ...attempt, ...patch } : attempt)),
    );
  };

  useEffect(() => {
    accumulatedSecondsRef.current = activeAttempt?.elapsedSeconds ?? 0;
    timerStartedAtRef.current = null;
    setTimerRunning(false);
    clearScheduledTimeouts();
  }, [activeAttemptIndex, clearScheduledTimeouts]);

  useEffect(() => {
    if (!timerRunning) {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    timerIntervalRef.current = window.setInterval(() => {
      const startedAt = timerStartedAtRef.current;
      if (startedAt == null) return;
      const nextElapsed = accumulatedSecondsRef.current + Math.floor((Date.now() - startedAt) / 1000);
      if (nextElapsed >= ISWT_MAX_DURATION_SECONDS) {
        accumulatedSecondsRef.current = ISWT_MAX_DURATION_SECONDS;
        patchActiveAttempt({
          elapsedSeconds: ISWT_MAX_DURATION_SECONDS,
          endedAt: new Date().toISOString(),
        });
        timerStartedAtRef.current = null;
        setTimerRunning(false);
        clearScheduledTimeouts();
        return;
      }
      patchActiveAttempt({ elapsedSeconds: nextElapsed });
    }, 250);

    return () => {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [clearScheduledTimeouts, timerRunning]);

  const iswtPayloadDraft = useMemo(
    () =>
      buildISWTPayload({
        attempts: attempts.filter(hasISWTAttemptData).map(getISWTAttemptDraft),
        notes: normalizedInitial?.notes,
        patientContext: patientContext ? { ...patientContext } : undefined,
      }),
    [attempts, normalizedInitial?.notes, patientContext],
  );
  const iswtSummary = useMemo(() => summarizeISWTResult(iswtPayloadDraft), [iswtPayloadDraft]);
  const iswtInterpretation = useMemo(() => resolveISWTInterpretation(iswtPayloadDraft), [iswtPayloadDraft]);

  const activeDistanceMeters = calculateISWTDistanceMeters(
    parseInteger(activeAttempt?.completedLevel ?? '') ?? 0,
    parseInteger(activeAttempt?.completedShuttlesOnLevel ?? '') ?? 0,
  );
  const activeTotalShuttles = calculateISWTCompletedShuttles(
    parseInteger(activeAttempt?.completedLevel ?? '') ?? 0,
    parseInteger(activeAttempt?.completedShuttlesOnLevel ?? '') ?? 0,
  );
  const currentLevelOptions = currentLevel ? `${currentLevel.speedKmh.toFixed(2)} km/h • ${currentLevel.shuttles} shuttle(s)` : 'Aguardando início';
  const currentLevelDefinition = getISWTLevelDefinition(parseInteger(activeAttempt?.completedLevel ?? '') ?? 0);

  const handleStart = async () => {
    const current = attempts[activeAttemptIndex];
    if (!current) return;

    if (timerRunning) return;
    accumulatedSecondsRef.current = current.elapsedSeconds;
    timerStartedAtRef.current = Date.now();
    patchActiveAttempt({
      startedAt: current.startedAt ?? new Date().toISOString(),
      endedAt: null,
    });
    await startFrom(current.elapsedSeconds);
    setTimerRunning(true);
  };

  const handlePause = () => {
    if (!timerRunning) return;
    const startedAt = timerStartedAtRef.current;
    const nextElapsed =
      startedAt == null ? accumulatedSecondsRef.current : accumulatedSecondsRef.current + Math.floor((Date.now() - startedAt) / 1000);
    accumulatedSecondsRef.current = nextElapsed;
    timerStartedAtRef.current = null;
    patchActiveAttempt({ elapsedSeconds: nextElapsed });
    setTimerRunning(false);
    clearScheduledTimeouts();
  };

  const handleFinish = () => {
    const startedAt = timerStartedAtRef.current;
    const nextElapsed =
      timerRunning && startedAt != null
        ? accumulatedSecondsRef.current + Math.floor((Date.now() - startedAt) / 1000)
        : activeAttempt.elapsedSeconds;
    accumulatedSecondsRef.current = nextElapsed;
    timerStartedAtRef.current = null;
    patchActiveAttempt({
      elapsedSeconds: nextElapsed,
      endedAt: new Date().toISOString(),
    });
    setTimerRunning(false);
    clearScheduledTimeouts();
  };

  const handleResetActiveAttempt = () => {
    clearScheduledTimeouts();
    setTimerRunning(false);
    timerStartedAtRef.current = null;
    accumulatedSecondsRef.current = 0;
    setAttempts((current) =>
      current.map((attempt, index) => (index === activeAttemptIndex ? buildISWTAttemptState(null, attempt.attemptNumber) : attempt)),
    );
  };

  const handleSave = () => {
    const recordedAttempts = attempts.filter(hasISWTAttemptData);
    if (!recordedAttempts.length) {
      toast.error('Registre pelo menos uma tentativa do ISWT.');
      return;
    }

    const payload = buildISWTPayload({
      attempts: recordedAttempts.map(getISWTAttemptDraft),
      notes: normalizedInitial?.notes,
      patientContext: patientContext ? { ...patientContext } : undefined,
    });
    onNext(payload);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-6 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-3xl">
            <div className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">ISWT padronizado</div>
            <h3 className="mt-2 text-2xl font-black tracking-tight text-slate-900">Incremental Shuttle Walk Test</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Pista reta de 10 m, pacing auditivo incremental, 12 níveis de 1 minuto e duas tentativas basais com intervalo recomendado de 30 minutos.
            </p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <div className="font-semibold">Solução para o áudio</div>
            <div className="mt-1">O app gera os beeps do protocolo no navegador, sem depender de upload de MP3.</div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Melhor distância" value={iswtSummary.bestDistanceMeters > 0 ? `${iswtSummary.bestDistanceMeters} m` : '—'} note={iswtSummary.bestLevelLabel} />
        <MetricCard label="Velocidade pico" value={iswtSummary.bestSpeedKmh != null ? `${iswtSummary.bestSpeedKmh.toFixed(2)} km/h` : '—'} note={iswtSummary.predictedDistanceMeters != null && iswtSummary.percentPredicted != null ? `${iswtSummary.percentPredicted.toFixed(1)}% do previsto` : 'Sem valor previsto'} />
        <MetricCard label="SpO2 mínima" value={iswtSummary.minSpo2 != null ? `${iswtSummary.minSpo2.toFixed(0)}%` : '—'} note={iswtSummary.peakHr != null ? `FC pico ${iswtSummary.peakHr.toFixed(0)} bpm` : 'Sem FC pico'} />
        <MetricCard label="Classificação" value={iswtSummary.severity} note={iswtSummary.learningEffectDetected ? 'Sugere efeito de aprendizagem' : 'Resultado conforme o protocolo'} />
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {attempts.map((attempt, index) => (
            <button
              key={`iswt-attempt-${attempt.attemptNumber}`}
              type="button"
              className={`rounded-full px-4 py-2 text-sm font-semibold ${index === activeAttemptIndex ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}
              onClick={() => !timerRunning && setActiveAttemptIndex(index)}
              disabled={timerRunning}
            >
              Tentativa {attempt.attemptNumber}
            </button>
          ))}
        </div>
        <div className="mt-4 text-sm text-slate-500">
          Distância calculada automaticamente a partir do nível final e do número de shuttles completos nesse nível.
        </div>
      </div>

      <PacerControls
        running={timerRunning}
        soundEnabled={soundEnabled}
        elapsedSeconds={activeAttempt.elapsedSeconds}
        maxDurationSeconds={ISWT_MAX_DURATION_SECONDS}
        onStart={() => void handleStart()}
        onPause={handlePause}
        onFinish={handleFinish}
        onReset={handleResetActiveAttempt}
        onToggleSound={() => setSoundEnabled((current) => !current)}
      />

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Pacer atual</div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <MetricCard label="Nível em curso" value={currentLevel ? `N${currentLevel.level}` : '—'} note={currentLevelOptions} />
            <MetricCard label="Tempo do shuttle" value={currentLevel ? `${currentLevel.shuttleTimeSeconds.toFixed(2)} s` : '—'} note="entre os beeps simples" />
          </div>
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <div className="font-semibold text-slate-900">Regras de parada</div>
            <div className="mt-1">Perda do pacing por segunda vez, SpO2 &lt; 80%, sintomas limitantes ou contraindicação clínica.</div>
          </div>
        </div>

        <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Registro da tentativa</div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <label className="space-y-1">
              <div className="text-xs font-medium text-slate-600">Nível final alcançado</div>
              <select
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={activeAttempt.completedLevel}
                onChange={(event) => patchActiveAttempt({ completedLevel: event.target.value, completedShuttlesOnLevel: '' })}
              >
                <option value="">Selecione</option>
                {Array.from({ length: 12 }, (_, index) => index + 1).map((level) => (
                  <option key={`iswt-level-${level}`} value={level}>
                    Nível {level}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <div className="text-xs font-medium text-slate-600">Shuttles completos no nível final</div>
              <select
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={activeAttempt.completedShuttlesOnLevel}
                onChange={(event) => patchActiveAttempt({ completedShuttlesOnLevel: event.target.value })}
                disabled={!currentLevelDefinition}
              >
                <option value="">Selecione</option>
                {Array.from({ length: currentLevelDefinition?.shuttles ?? 0 }, (_, index) => index + 1).map((shuttle) => (
                  <option key={`iswt-shuttle-${shuttle}`} value={shuttle}>
                    {shuttle}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <div className="text-xs font-medium text-slate-600">SpO2 mínima observada (%)</div>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={activeAttempt.observedMinSpo2}
                onChange={(event) => patchActiveAttempt({ observedMinSpo2: event.target.value })}
              />
            </label>
            <label className="space-y-1">
              <div className="text-xs font-medium text-slate-600">FC pico observada (bpm)</div>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={activeAttempt.observedPeakHr}
                onChange={(event) => patchActiveAttempt({ observedPeakHr: event.target.value })}
              />
            </label>
            <label className="space-y-1 md:col-span-2">
              <div className="text-xs font-medium text-slate-600">Motivo de encerramento</div>
              <select
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={activeAttempt.stopReason}
                onChange={(event) => patchActiveAttempt({ stopReason: event.target.value as StepTestStopReason })}
              >
                {STOP_REASON_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <MetricCard label="Shuttles totais" value={activeTotalShuttles > 0 ? String(activeTotalShuttles) : '—'} note="ida ou volta de 10 m" />
            <MetricCard label="Distância calculada" value={activeDistanceMeters > 0 ? `${activeDistanceMeters} m` : '—'} note={activeAttempt.completedLevel ? formatISWTLevelLabel(parseInteger(activeAttempt.completedLevel) ?? 0, parseInteger(activeAttempt.completedShuttlesOnLevel) ?? 0) : 'Selecione nível e shuttles'} />
          </div>
          <label className="mt-4 block space-y-1">
            <div className="text-xs font-medium text-slate-600">Observações</div>
            <textarea
              className="min-h-[110px] w-full rounded-2xl border border-slate-200 px-3 py-3 text-sm"
              value={activeAttempt.notes}
              onChange={(event) => patchActiveAttempt({ notes: event.target.value })}
              placeholder="Ex.: falhou por segunda vez ao chegar ao cone, dispneia intensa, uso de O2, sintomas associados..."
            />
          </label>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <VitalsEditor
          title={`Pré-teste | tentativa ${activeAttempt.attemptNumber}`}
          state={activeAttempt.baseline}
          onChange={(patch) => patchActiveAttempt({ baseline: { ...activeAttempt.baseline, ...patch } })}
        />
        <VitalsEditor
          title={`Pós-teste | tentativa ${activeAttempt.attemptNumber}`}
          state={activeAttempt.final}
          onChange={(patch) => patchActiveAttempt({ final: { ...activeAttempt.final, ...patch } })}
        />
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Interpretação automática</div>
        <p className="mt-4 text-sm leading-relaxed text-slate-600">{iswtInterpretation}</p>
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 shadow-sm">
        <div className="flex items-start gap-3 text-sm text-slate-600">
          <Info className="mt-0.5 h-4 w-4 text-slate-400" />
          <div>
            <div className="font-semibold text-slate-900">Referência usada</div>
            <div className="mt-1">{ISWT_PROTOCOL_METADATA.description}</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-3">
        {onCancel ? (
          <button type="button" className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700" onClick={onCancel}>
            Cancelar
          </button>
        ) : null}
        <button type="button" className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white" onClick={handleSave}>
          Salvar ISWT
        </button>
      </div>
    </div>
  );
}

export function ESWTTest({ initial = null, patientContext, iswtReference = null, onCancel, onNext }: ESWTTestProps) {
  const normalizedInitial = normalizeESWTPayload(initial);
  const [form, setForm] = useState<ESWTFormState>(() => buildESWTState(normalizedInitial, iswtReference));
  const [timerRunning, setTimerRunning] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const sourceIswtSpeedKmh = parseNullableNumber(form.sourceIswtSpeedKmh) ?? 0;
  const sourceIswtLevel = parseInteger(form.sourceIswtLevel);
  const protocol: ESWTProtocol = useMemo(
    () =>
      buildDefaultESWTProtocol({
        sourceIswtLevel,
        sourceIswtSpeedKmh,
        speedPercentOfIswt: form.speedPercentOfIswt,
      }),
    [form.speedPercentOfIswt, sourceIswtLevel, sourceIswtSpeedKmh],
  );
  const pacingEvents = useMemo(() => buildESWTPacingEvents(protocol), [protocol]);
  const { clearScheduledTimeouts, startFrom } = useScheduledBeeps({
    events: pacingEvents,
    muted: !soundEnabled,
  });

  const timerStartedAtRef = useRef<number | null>(null);
  const accumulatedSecondsRef = useRef<number>(form.elapsedSeconds);
  const timerIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (timerRunning) return;
    accumulatedSecondsRef.current = form.elapsedSeconds;
  }, [form.elapsedSeconds, timerRunning]);

  useEffect(() => {
    if (!timerRunning) {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    timerIntervalRef.current = window.setInterval(() => {
      const startedAt = timerStartedAtRef.current;
      if (startedAt == null) return;
      const nextElapsed = accumulatedSecondsRef.current + Math.floor((Date.now() - startedAt) / 1000);
      if (nextElapsed >= protocol.maxDurationSeconds) {
        accumulatedSecondsRef.current = protocol.maxDurationSeconds;
        setForm((current) => ({
          ...current,
          elapsedSeconds: protocol.maxDurationSeconds,
          endedAt: new Date().toISOString(),
        }));
        timerStartedAtRef.current = null;
        setTimerRunning(false);
        clearScheduledTimeouts();
        return;
      }
      setForm((current) => ({ ...current, elapsedSeconds: nextElapsed }));
    }, 250);

    return () => {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [clearScheduledTimeouts, protocol.maxDurationSeconds, timerRunning]);

  const eswtPayloadDraft = useMemo(
    () =>
      buildESWTPayload({
        protocol,
        baseline: toVitalsPayload(form.baseline),
        final: toVitalsPayload(form.final),
        observedPeakHr: parseNullableNumber(form.observedPeakHr),
        observedMinSpo2: parseNullableNumber(form.observedMinSpo2),
        totalTimeSeconds: form.elapsedSeconds,
        stopReason: form.stopReason,
        timer: toTimerBlock(form.elapsedSeconds, form.startedAt, form.endedAt, protocol.maxDurationSeconds),
        notes: form.notes,
        patientContext: patientContext ? { ...patientContext } : undefined,
      }),
    [form, patientContext, protocol],
  );
  const eswtSummary = useMemo(() => summarizeESWTResult(eswtPayloadDraft), [eswtPayloadDraft]);
  const eswtInterpretation = useMemo(() => resolveESWTInterpretation(eswtPayloadDraft), [eswtPayloadDraft]);

  const handleStart = async () => {
    if (timerRunning) return;
    if (protocol.targetSpeedKmh <= 0) {
      toast.error('Informe a velocidade pico do ISWT para derivar o pacing do ESWT.');
      return;
    }

    accumulatedSecondsRef.current = form.elapsedSeconds;
    timerStartedAtRef.current = Date.now();
    setForm((current) => ({
      ...current,
      startedAt: current.startedAt ?? new Date().toISOString(),
      endedAt: null,
    }));
    await startFrom(form.elapsedSeconds);
    setTimerRunning(true);
  };

  const handlePause = () => {
    if (!timerRunning) return;
    const startedAt = timerStartedAtRef.current;
    const nextElapsed =
      startedAt == null ? accumulatedSecondsRef.current : accumulatedSecondsRef.current + Math.floor((Date.now() - startedAt) / 1000);
    accumulatedSecondsRef.current = nextElapsed;
    timerStartedAtRef.current = null;
    setForm((current) => ({ ...current, elapsedSeconds: nextElapsed }));
    setTimerRunning(false);
    clearScheduledTimeouts();
  };

  const handleFinish = () => {
    const startedAt = timerStartedAtRef.current;
    const nextElapsed =
      timerRunning && startedAt != null ? accumulatedSecondsRef.current + Math.floor((Date.now() - startedAt) / 1000) : form.elapsedSeconds;
    accumulatedSecondsRef.current = nextElapsed;
    timerStartedAtRef.current = null;
    setForm((current) => ({
      ...current,
      elapsedSeconds: nextElapsed,
      endedAt: new Date().toISOString(),
    }));
    setTimerRunning(false);
    clearScheduledTimeouts();
  };

  const handleReset = () => {
    clearScheduledTimeouts();
    setTimerRunning(false);
    timerStartedAtRef.current = null;
    accumulatedSecondsRef.current = 0;
    setForm(buildESWTState(null, iswtReference));
  };

  const handleSave = () => {
    if (protocol.targetSpeedKmh <= 0) {
      toast.error('Derive o pacing do ESWT a partir da velocidade do ISWT antes de salvar.');
      return;
    }
    if (form.elapsedSeconds <= 0) {
      toast.error('Registre o tempo tolerado no ESWT.');
      return;
    }
    onNext(eswtPayloadDraft);
  };

  const targetDistanceNote = protocol.targetSpeedKmh > 0 ? `${protocol.targetSpeedKmh.toFixed(2)} km/h` : 'Informe a velocidade do ISWT';

  return (
    <div className="space-y-6">
      <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-6 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-3xl">
            <div className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">ESWT padronizado</div>
            <h3 className="mt-2 text-2xl font-black tracking-tight text-slate-900">Endurance Shuttle Walk Test</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Pista reta de 10 m, pacing auditivo constante e velocidade derivada do ISWT. O default sugerido é 85% da velocidade pico do teste incremental.
            </p>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            <div className="font-semibold">Pacing sem upload</div>
            <div className="mt-1">O navegador sintetiza os beeps e mantém a pista visual mesmo se o som estiver bloqueado.</div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Velocidade alvo" value={protocol.targetSpeedKmh > 0 ? `${protocol.targetSpeedKmh.toFixed(2)} km/h` : '—'} note={`${protocol.speedPercentOfIswt}% da velocidade do ISWT`} />
        <MetricCard label="Tempo tolerado" value={form.elapsedSeconds > 0 ? formatClock(form.elapsedSeconds) : '—'} note={eswtSummary.distanceMeters > 0 ? `${eswtSummary.distanceMeters} m estimados` : 'Sem distância estimada'} />
        <MetricCard label="SpO2 mínima" value={eswtSummary.minSpo2 != null ? `${eswtSummary.minSpo2.toFixed(0)}%` : '—'} note={eswtSummary.peakHr != null ? `FC pico ${eswtSummary.peakHr.toFixed(0)} bpm` : 'Sem FC pico'} />
        <MetricCard label="Classificação" value={eswtSummary.severity} note={eswtSummary.withinRecommendedWindow == null ? 'Sem faixa alvo' : eswtSummary.withinRecommendedWindow ? 'Entre 3 e 8 min' : 'Fora da faixa 3-8 min'} />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Derivação da velocidade</div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <label className="space-y-1">
              <div className="text-xs font-medium text-slate-600">Velocidade pico do ISWT (km/h)</div>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={form.sourceIswtSpeedKmh}
                onChange={(event) => setForm((current) => ({ ...current, sourceIswtSpeedKmh: event.target.value }))}
                placeholder="Ex.: 5,47"
              />
            </label>
            <label className="space-y-1">
              <div className="text-xs font-medium text-slate-600">Nível de origem do ISWT</div>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={form.sourceIswtLevel}
                onChange={(event) => setForm((current) => ({ ...current, sourceIswtLevel: event.target.value }))}
                placeholder="Ex.: 7"
              />
            </label>
            <label className="space-y-1 md:col-span-2">
              <div className="text-xs font-medium text-slate-600">% da velocidade pico do ISWT</div>
              <select
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={form.speedPercentOfIswt}
                onChange={(event) => setForm((current) => ({ ...current, speedPercentOfIswt: Number(event.target.value) }))}
              >
                {ESWT_SPEED_PERCENT_OPTIONS.map((option) => (
                  <option key={`eswt-percent-${option}`} value={option}>
                    {option}%
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <div className="font-semibold text-slate-900">Pacing calculado</div>
            <div className="mt-1">Velocidade alvo: {targetDistanceNote}. Intervalo entre shuttles: {protocol.shuttleIntervalSeconds > 0 ? `${protocol.shuttleIntervalSeconds.toFixed(2)} s` : '—'}.</div>
          </div>
          {!iswtReference?.peakSpeedKmh ? (
            <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4" />
              <div>Sem ISWT de referência na sessão atual. Você pode informar manualmente a velocidade pico do ISWT para repetir o ESWT.</div>
            </div>
          ) : null}
        </div>

        <PacerControls
          running={timerRunning}
          soundEnabled={soundEnabled}
          elapsedSeconds={form.elapsedSeconds}
          maxDurationSeconds={protocol.maxDurationSeconds}
          onStart={() => void handleStart()}
          onPause={handlePause}
          onFinish={handleFinish}
          onReset={handleReset}
          onToggleSound={() => setSoundEnabled((current) => !current)}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Registro do teste</div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <label className="space-y-1">
              <div className="text-xs font-medium text-slate-600">SpO2 mínima observada (%)</div>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={form.observedMinSpo2}
                onChange={(event) => setForm((current) => ({ ...current, observedMinSpo2: event.target.value }))}
              />
            </label>
            <label className="space-y-1">
              <div className="text-xs font-medium text-slate-600">FC pico observada (bpm)</div>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={form.observedPeakHr}
                onChange={(event) => setForm((current) => ({ ...current, observedPeakHr: event.target.value }))}
              />
            </label>
            <label className="space-y-1 md:col-span-2">
              <div className="text-xs font-medium text-slate-600">Motivo de encerramento</div>
              <select
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={form.stopReason}
                onChange={(event) => setForm((current) => ({ ...current, stopReason: event.target.value as StepTestStopReason }))}
              >
                {STOP_REASON_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <MetricCard label="Shuttles estimados" value={eswtPayloadDraft.totalShuttles > 0 ? String(eswtPayloadDraft.totalShuttles) : '—'} note="com base no pacing constante" />
            <MetricCard label="Distância estimada" value={eswtSummary.distanceMeters > 0 ? `${eswtSummary.distanceMeters} m` : '—'} note={protocol.targetSpeedKmh > 0 ? `${protocol.targetSpeedKmh.toFixed(2)} km/h` : 'Sem velocidade alvo'} />
          </div>
          <label className="mt-4 block space-y-1">
            <div className="text-xs font-medium text-slate-600">Observações</div>
            <textarea
              className="min-h-[110px] w-full rounded-2xl border border-slate-200 px-3 py-3 text-sm"
              value={form.notes}
              onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
              placeholder="Ex.: interrupção por dispneia, necessidade de O2, pacing mantido até 06:40, sintomas associados..."
            />
          </label>
        </div>

        <div className="space-y-6">
          <VitalsEditor
            title="Pré-teste"
            state={form.baseline}
            onChange={(patch) => setForm((current) => ({ ...current, baseline: { ...current.baseline, ...patch } }))}
          />
          <VitalsEditor
            title="Pós-teste"
            state={form.final}
            onChange={(patch) => setForm((current) => ({ ...current, final: { ...current.final, ...patch } }))}
          />
        </div>
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Interpretação automática</div>
        <p className="mt-4 text-sm leading-relaxed text-slate-600">{eswtInterpretation}</p>
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 shadow-sm">
        <div className="flex items-start gap-3 text-sm text-slate-600">
          <Info className="mt-0.5 h-4 w-4 text-slate-400" />
          <div>
            <div className="font-semibold text-slate-900">Referência usada</div>
            <div className="mt-1">{ESWT_PROTOCOL_METADATA.description}</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-3">
        {onCancel ? (
          <button type="button" className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700" onClick={onCancel}>
            Cancelar
          </button>
        ) : null}
        <button type="button" className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white" onClick={handleSave}>
          Salvar ESWT
        </button>
      </div>
    </div>
  );
}
